// Settings versioning. Every save is a new version; engines take a version
// explicitly; the diff is what the audit log and the "rules changed" feed
// post render.

import { z } from 'zod';
import { LeagueSettings } from './schemas';

export const SettingsDiffEntry = z.object({
  path: z.string(),
  from: z.unknown(),
  to: z.unknown(),
});
export type SettingsDiffEntry = z.infer<typeof SettingsDiffEntry>;

export const LeagueSettingsVersion = z.object({
  leagueId: z.string().min(1),
  version: z.number().int().min(1),
  settings: LeagueSettings,
  createdAt: z.string().datetime({ offset: true }),
  /** User id, or 'system' for migrations. */
  createdBy: z.string().min(1),
  reason: z.string().max(500).default(''),
  diff: z.array(SettingsDiffEntry).default([]),
  effectiveFromPeriod: z.number().int().min(1),
  approvedBy: z.array(z.string()).default([]),
});
export type LeagueSettingsVersion = z.infer<typeof LeagueSettingsVersion>;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Leaf-level diff between two settings objects. Arrays are compared whole
 * (a changed scoring rule list shows as one entry at 'scoring.units'), which
 * matches how the editor and the feed post talk about them.
 */
export function diffSettings(from: unknown, to: unknown, path = ''): SettingsDiffEntry[] {
  if (isPlainObject(from) && isPlainObject(to)) {
    const keys = new Set([...Object.keys(from), ...Object.keys(to)]);
    const out: SettingsDiffEntry[] = [];
    for (const k of [...keys].sort()) {
      out.push(...diffSettings(from[k], to[k], path ? `${path}.${k}` : k));
    }
    return out;
  }
  return JSON.stringify(from) === JSON.stringify(to) ? [] : [{ path, from, to }];
}
